/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, March 13th 2022, 1:47:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as dateFNS from "date-fns";
import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import FormControl from "@mui/material/FormControl";
import { FormField, FIELD_TYPES } from "lib/schemas";
import { TextFormInputWithTagSelector } from "common/components/context-tags";
import { NumberingSequenceForm } from "common/components/numbering-sequence";
import { useFormBuilderContext } from "form-builder/providers";
import { getDefaultNumberingSequence } from "lib/factories";
import {
  TextFormInput,
  SwitchFormInput,
  DateFormInput,
  CommaSeparatedTagsFormInput,
  InputHelpDialogWrapper,
  StyledOSDialog,
  OSActionButton,
  DropDownFormInput,
  EmphasisText,
} from "ui-widgets";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { COLORSCHEME } from "theme/styles";
import { TranslatableTextFormInput } from "translation/components";
import { DateMinMaxSettings, DOBMinMaxAgeSettings } from "./DateMinMaxSettings";
import { ChoiceFieldSettings } from "./ChoiceFieldSettings";
import { DynamicMatrixFieldSettings } from "./DynamicMatrixFieldSettings";
import { FieldMany2OnePicker } from "./FieldMany2OnePicker";
import { FormMany2OnePicker } from "./FormMany2OnePicker";
import { getFieldTypeWidgets } from "./FieldTypeWidgets";
import { GeoFieldLocationDataTargetConfig } from "./GeoFieldLocationDataTargetConfig";

import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";

export const FieldTypeSettings = ({ field_type }: Pick<FormField, "field_type">) => {
  switch (field_type) {
    case FIELD_TYPES.TEXT:
      return <TextFieldSettings />;

    case FIELD_TYPES.TEXTAREA:
      return <TextAreaFieldSettings />;

    case FIELD_TYPES.EMAIL:
      return <EmailFieldSettings />;

    case FIELD_TYPES.CHECKBOX:
      return <CheckBoxFieldSettings />;

    case FIELD_TYPES.CHECKBOX_MULTIPLE:
      return <CheckBoxMultipleFieldSettings />;

    case FIELD_TYPES.SELECT:
      return <SelectFieldSettings />;

    case FIELD_TYPES.SELECT_MULTIPLE:
      return <SelectMultipleFieldSettings />;

    case FIELD_TYPES.RADIO_MULTIPLE:
      return <RadioMultipleFieldSettings />;

    case FIELD_TYPES.FILE:
      return <FileFieldSettings />;

    case FIELD_TYPES.DATE:
      return <DateFieldSettings />;

    case FIELD_TYPES.DATE_TIME:
      return <DateTimeFieldSettings />;

    case FIELD_TYPES.HIDDEN:
      return <HiddenFieldSettings />;

    case FIELD_TYPES.NUMBER:
      return <NumberFieldSettings />;

    case FIELD_TYPES.URL:
      return (
        <>
          <DefaultValueSetting />
          <URLFieldSettings />
        </>
      );

    case FIELD_TYPES.DOB:
      return (
        <>
          <DefaultValueSetting />
          <DOBMinMaxAgeSettings />
        </>
      );

    case FIELD_TYPES.SECTION_BREAK:
      return <SectionBreakFieldSettings />;

    // case FIELD_TYPES.GEO_COORDINATES:
    //   return <GISCoordinatesFieldSettings />;

    case FIELD_TYPES.MULTIPLE_FILE:
      return <MultipleFileFieldSettings />;

    case FIELD_TYPES.PAGE_TITLE:
      return <PageTitleFieldSettings />;

    // case FIELD_TYPES.BUTTON_CHECK:
    //   return <ButtonCheckSettings />;

    case FIELD_TYPES.MATRIX:
      return <MatrixFieldSettings />;

    case FIELD_TYPES.FORM_ENTRIES:
      return <FormEntriesFieldSettings />;

    case FIELD_TYPES.TITLE:
      return <TitleFieldSettings />;

    case FIELD_TYPES.MULTI_FORM_ENTRIES:
      return <MultipleFormEntriesFieldSettings />;

    case FIELD_TYPES.AUTO_COMPLETE:
      return <AutoCompleteFieldSettings />;

    case FIELD_TYPES.SOURCE_COMPLETE:
      return <SourceCompleteFieldSettings />;

    // case FIELD_TYPES.MATRIX_VIEW:
    //   return <MatrixViewFieldSettings />;

    case FIELD_TYPES.SIGNATURE_PAD:
      return <SignaturePadFieldSettings />;

    case FIELD_TYPES.IMAGE:
      return <ImageFieldSettings />;

    case FIELD_TYPES.PHONE_NUMBER:
      return <PhoneNumberFieldSettings />;

    case FIELD_TYPES.DYNAMIC_MATRIX:
      return (
        <>
          <DynamicMatrixFieldSettings />
          <DefaultValueSetting />
        </>
      );

    case FIELD_TYPES.GEO_COORDINATES:
      return <GeoFieldSettings />;

    case FIELD_TYPES.AUTO_GENERATED_SEQUENCE:
      return <AutoSequenceFieldSettings />;

    default:
      return <DefaultValueSetting />;
  }
};

/** Component that displays a message to user for features that are not yet supported.
 * This is because the OneStop backend is ahead of the frontend in terms of feature support
 * due to it existing before going SaaS. So there are some advanced features that are available in
 * the backend that are yet to be implemented in the frontend e.g. ability to configure FormEntries fields,
 * ability to configure custom validators, ability to setup managed data sets.
 *
 * This component is needed because in our service templates, some of these features maybe available
 * for frontend users to use but are not yet support for configuration/customization.
 */
const NotYetSupportedComponent = () => (
  <Alert severity="info">
    <AlertTitle>Not yet supported</AlertTitle>
    <p>Configuration and customization of this feature is not yet supported. We are working on it.</p>
    <p>Submitters can however be able to use this feature with no problem.</p>
    <p>Drop us an email if you want to be able to configure this sooner!</p>
  </Alert>
);

const PlaceHolderSetting = () => (
  <TranslatableTextFormInput<FormField> label="Placeholder Text" schema_field_key="placeholder_text" />
);
const DefaultValueSetting = () => {
  const { contextTagsAPIFilters = {} } = useFormBuilderContext();
  return (
    <>
      <TextFormInputWithTagSelector<FormField>
        label="Default Value"
        schema_field_key="default"
        contextTagsAPIFilters={contextTagsAPIFilters}
      />
    </>
  );
};

const TextFieldSettings = () => (
  <Grid container>
    <PlaceHolderSetting />
    <DefaultValueSetting />
    <TextFormInput<FormField> label="Character Length" schema_field_key="char_length" type="number" />
  </Grid>
);

const TextAreaFieldSettings = () => {
  const { setValue, watch } = useOSFormContext<FormField>();
  return (
    <>
      <TextFieldSettings />{" "}
      <FormControlLabel
        control={
          <Switch
            onChange={(event, checked) => {
              if (checked) setValue("widget", "WYSIWYG", { shouldDirty: true });
              if (!checked) setValue("widget", null, { shouldDirty: true });
            }}
            checked={watch("widget") === "WYSIWYG"}
          />
        }
        label="Enable WYSIWYG Editor"
      />
    </>
  );
};

const EmailFieldSettings = () => (
  <Grid container>
    <SwitchFormInput<FormField>
      label="CC this address in all email notifications to the submitter?"
      schema_field_key="copy_when_sending_notifications"
    />
    <TextFieldSettings />
  </Grid>
);

const CheckBoxFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <ChoiceFieldSettings />
  </>
);

const CheckBoxMultipleFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <ChoiceFieldSettings />
  </>
);

const SelectFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <ChoiceFieldSettings />
  </>
);

const SelectMultipleFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <ChoiceFieldSettings />
  </>
);

const RadioMultipleFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <ChoiceFieldSettings />
  </>
);

const FileFieldSettings = () => {
  const { setValue, watch } = useOSFormContext<FormField>();
  return (
    <>
      <CommaSeparatedTagsFormInput<FormField>
        schema_field_key="allowed_file_extensions"
        label="Allowed File Extensions"
      />
      <CommaSeparatedTagsFormInput<FormField>
        schema_field_key="blocked_file_extensions"
        label="Blocked File Extensions"
      />
      <TextFormInput<FormField> schema_field_key="max_file_size" label="Maximum File Size (MB)" type="number" />

      <InputHelpDialogWrapper
        helpTitle="In-App File Viewer"
        helpContent="Allow users to view uploaded files within the browser"
      >
        <FormControlLabel
          control={
            <Switch
              onChange={(event, checked) => {
                if (checked) setValue("widget", "BROWSER_VIEW", { shouldDirty: true });
                if (!checked) setValue("widget", null, { shouldDirty: true });
              }}
              checked={watch("widget") === "BROWSER_VIEW"}
            />
          }
          label="Enable In-App File Viewer"
        />
      </InputHelpDialogWrapper>
    </>
  );
};

const MultipleFileFieldSettings = () => <FileFieldSettings />;

type DateMinMaxSettingsType = "fixed_date" | "relative_to_entry_date"; // TODO: add more types here i.e. for month only, year only, etc

const FixedMinMaxDateSettings = () => {
  const strFormat = "yyyy-MM-dd";
  const parseDate = (date) => {
    if (date instanceof Date) {
      return dateFNS.format(date, strFormat);
    }
    return date;
  };

  return (
    <>
      <DateFormInput<FormField>
        inputFormat={strFormat}
        parseDate={parseDate}
        schema_field_key="min_value"
        label="Minimum Date"
      />
      <DateFormInput<FormField>
        inputFormat={strFormat}
        parseDate={parseDate}
        schema_field_key="max_value"
        label="Maximum Date"
      />
    </>
  );
};

const DateSettingsTypePicker = ({ children: relativeToEntryComponent }: { children: React.ReactNode }) => {
  const { watch } = useOSFormContext<FormField>();
  const dateMinMaxType: DateMinMaxSettingsType = watch("extra_configurations.date_min_max_settings_type");
  let settingsComponent = relativeToEntryComponent;

  switch (dateMinMaxType) {
    case "fixed_date":
      settingsComponent = <FixedMinMaxDateSettings />;
      break;
    case "relative_to_entry_date":
      settingsComponent = relativeToEntryComponent;
      break;
  }

  return (
    <>
      <DropDownFormInput
        label="How do you want to set the Date Limitations?"
        schema_field_key="extra_configurations.date_min_max_settings_type"
        options={
          {
            relative_to_entry_date: "Relative to Date / Time a User fills in the form",
            fixed_date: "Fixed Date",
          } as Record<DateMinMaxSettingsType, string>
        }
      />
      {settingsComponent}
    </>
  );
};

const DateFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <DateSettingsTypePicker>
      <DateMinMaxSettings
        schema_field_key="max_value"
        label="Maximum Date Limitation"
        limitationType="max"
        field_type={FIELD_TYPES.DATE}
      />
      <DateMinMaxSettings
        schema_field_key="min_value"
        label="Minimum Date Limitation"
        limitationType="min"
        field_type={FIELD_TYPES.DATE}
      />
    </DateSettingsTypePicker>
  </>
);

const DateTimeFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <DateSettingsTypePicker>
      <DateMinMaxSettings
        schema_field_key="max_value"
        label="Maximum Date/Time Limitation"
        limitationType="max"
        field_type={FIELD_TYPES.DATE_TIME}
      />
      <DateMinMaxSettings
        schema_field_key="min_value"
        label="Minimum Date/Time Limitation"
        limitationType="min"
        field_type={FIELD_TYPES.DATE_TIME}
      />
    </DateSettingsTypePicker>
  </>
);

const NumberFieldSettings = () => (
  <>
    <DefaultValueSetting />
    <TextFormInput<FormField> schema_field_key="max_value" label="Maximum Value" type="number" />
    <TextFormInput<FormField> schema_field_key="min_value" label="Minimum Value" type="number" />
  </>
);

const HiddenFieldSettings = () => <DefaultValueSetting />;

const URLFieldSettings = () => <></>; // Just in case to avoid null errors

const SectionBreakFieldSettings = () => <></>; // Just in case to avoid null errors

const PageTitleFieldSettings = () => <></>; // Just in case to avoid null errors

const TitleFieldSettings = () => <></>; // Just in case to avoid null errors

const MatrixFieldSettings = () => <NotYetSupportedComponent />;

const FormEntriesFieldSettings = () => {
  const { watch } = useOSFormContext<FormField>();
  const watchedEntriesFormId = watch("entries_form");

  return (
    <>
      <DefaultValueSetting />
      <InputHelpDialogWrapper
        helpTitle="Submissions Form"
        helpContent="Choose the form whose submissions you want to be loaded in this field for the user to select from."
      >
        <FormMany2OnePicker<FormField> schema_field_key="entries_form" label="Submissions Form" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Submission Label Field"
        helpContent="Choose a field in the submissions form that will be used to display the option Label for each submission that the user can choose from."
      >
        <FieldMany2OnePicker<FormField>
          schema_field_key="entry_display_field"
          label="Submission Label Field"
          apiQueryParams={{ form: watchedEntriesFormId }}
        />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Show Submissions for Logged in User Only"
        helpContent="For a Form Submissions Field type, this setting ensures that only form submissions for the logged in user are listed in the dropdown choices of form submissions."
      >
        <SwitchFormInput<FormField> schema_field_key="bind_user" label="Show Submissions for Logged in User Only" />
      </InputHelpDialogWrapper>
      <InputHelpDialogWrapper
        helpTitle="Update Linked Fields"
        helpContent="Selecting this will result in the selected submission for this field auto filling all the linked fields in this form."
      >
        <SwitchFormInput<FormField>
          schema_field_key="use_related"
          label="Use selected submission data to Update Linked Fields"
        />
      </InputHelpDialogWrapper>
    </>
  );
};

const MultipleFormEntriesFieldSettings = () => <FormEntriesFieldSettings />;

const AutoCompleteFieldSettings = () => <NotYetSupportedComponent />;

const SourceCompleteFieldSettings = () => <NotYetSupportedComponent />;

const SignaturePadFieldSettings = () => <></>; // Just in case to avoid null errors

const ImageFieldSettings = () => <FileFieldSettings />;

const PhoneNumberFieldSettings = () => (
  <>
    <DefaultValueSetting />
  </>
); // Just in case to avoid null errors

const GeoFieldSettings = () => (
  <Grid container>
    <SwitchFormInput label="Show Map" schema_field_key="extra_configurations.geo_field_settings.show_map" />
    <SwitchFormInput
      label="Default to User's Current Location?"
      schema_field_key="extra_configurations.geo_field_settings.default_to_current_location"
    />
    <TextFormInput
      label="Default Latitude"
      schema_field_key="extra_configurations.geo_field_settings.default_latitude"
    />
    <TextFormInput
      label="Default Longitude"
      schema_field_key="extra_configurations.geo_field_settings.default_longitude"
    />
    <SwitchFormInput
      label="Update Location Data using Reverse Geo Coding?"
      schema_field_key="extra_configurations.geo_field_settings.update_location_data_on_reverse_geocode"
    />
    <DefaultValueSetting />
    <FormControl>
      <EmphasisText>Location Data Target Fields</EmphasisText>
      <GeoFieldLocationDataTargetConfig />
    </FormControl>
  </Grid>
);

const AutoSequenceFieldSettings = () => {
  const { watch, setValue, getValues } = useOSFormContext<FormField>();
  const [, iconComponent] = getFieldTypeWidgets(FIELD_TYPES.AUTO_GENERATED_SEQUENCE);
  const newSequenceDefault = getDefaultNumberingSequence({
    organizationId: watch("artifact_org"),
    objectName: `${watch("label")}`,
  });
  return (
    <StyledOSDialog
      onSubmit={(e) => {
        e.stopPropagation();
      }}
      popUpIcon={<div style={{ color: COLORSCHEME.primary }}>{iconComponent}</div>}
      popUpTitle="Configure Auto Sequence"
      renderOpenButton={({ handleOpen }) => (
        <OSActionButton fullWidth size="large" actionStyle="configure" onClick={handleOpen}>
          Configure Sequence
        </OSActionButton>
      )}
    >
      <NumberingSequenceForm
        numberingSequenceId={watch("auto_generate_sequence")}
        numberingSequenceDefault={newSequenceDefault}
        onAPIResponse={(numberingSequence) => {
          if (numberingSequence?.id !== getValues("auto_generate_sequence")) {
            setValue("auto_generate_sequence", numberingSequence?.id, { shouldDirty: true });
          }
        }}
      />
    </StyledOSDialog>
  );
};
